import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldAlert, Info, HelpCircle, Check, X, Hourglass } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Unified Block/Report-Problem modal.
 * Props:
 *   item:    the line item being flagged (contains .stage)
 *   onClose: () => void
 *   onBlock: ({ block_reason, block_reason_code, block_reason_subteam }) => Promise<void>|void
 */
export default function BlockModal({ item, onClose, onBlock }) {
  const [catalogue, setCatalogue] = useState(null);
  const [subteam, setSubteam] = useState("");
  const [code, setCode] = useState("");
  const [comment, setComment] = useState("");
  const [codeUnknown, setCodeUnknown] = useState(false);
  const [freeText, setFreeText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Accountability tagging — who this block is WAITING ON (they get a bell
  // mention; demandNextAction asks them to come specify the next action).
  const [mentionables, setMentionables] = useState([]);
  const [waitingOn, setWaitingOn] = useState([]);   // [{id, name, role}]
  const [waitQuery, setWaitQuery] = useState("");
  const [demandNextAction, setDemandNextAction] = useState(true);

  const stage = item?.stage || "";
  const stageCfg = catalogue?.[stage];
  const isDispatch = stage === "Dispatch";
  const usesSubteam = !!stageCfg?.uses_subteam;

  // Fetch catalogue once per modal open
  useEffect(() => {
    if (!item) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/reason-codes`);
        if (!cancelled) setCatalogue(res.data);
      } catch (e) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [item]);

  // Reset local state when modal re-opens for a new item
  useEffect(() => {
    if (item) {
      setSubteam("");
      setCode("");
      setComment("");
      setCodeUnknown(false);
      setFreeText("");
      setWaitingOn([]);
      setWaitQuery("");
      setDemandNextAction(true);
    }
  }, [item]);

  // Light user list for the "waiting on" picker (same endpoint @mentions use)
  useEffect(() => {
    if (!item) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/users/mentionables`);
        if (!cancelled) setMentionables(Array.isArray(res.data) ? res.data : []);
      } catch (e) { /* silent — tagging simply unavailable */ }
    })();
    return () => { cancelled = true; };
  }, [item]);

  const waitMatches = useMemo(() => {
    const q = waitQuery.trim().toLowerCase();
    if (!q) return [];
    const chosen = new Set(waitingOn.map(u => u.id));
    return mentionables
      .filter(u => !chosen.has(u.id) && ((u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q)))
      .slice(0, 6);
  }, [waitQuery, mentionables, waitingOn]);

  const activeCodes = useMemo(() => {
    if (!stageCfg) return [];
    if (usesSubteam) return subteam ? (stageCfg.subteams?.[subteam] || []) : [];
    return stageCfg.codes || [];
  }, [stageCfg, usesSubteam, subteam]);

  const selectedMeta = useMemo(
    () => activeCodes.find(c => c.code === code) || null,
    [activeCodes, code]
  );

  // Reset code when sub-team switches (codes are sub-team-specific)
  const handleSubteamChange = (val) => {
    setSubteam(val);
    setCode("");
  };

  const canSubmit = (() => {
    if (submitting) return false;
    if (isDispatch) return freeText.trim().length >= 3;
    if (codeUnknown) return freeText.trim().length >= 3;
    if (usesSubteam && !subteam) return false;
    return !!code;
  })();

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      let payload;
      if (isDispatch) {
        payload = { block_reason: freeText.trim(), block_reason_code: "", block_reason_subteam: "" };
      } else if (codeUnknown) {
        payload = { block_reason: freeText.trim(), block_reason_code: "", block_reason_subteam: "" };
      } else {
        const desc = selectedMeta?.description || "";
        const note = comment.trim();
        const combined = note ? `${desc} — ${note}` : desc;
        payload = {
          block_reason: combined,
          block_reason_code: code,
          block_reason_subteam: usesSubteam ? subteam : "",
        };
      }
      payload.waiting_on_user_ids = waitingOn.map(u => u.id);
      payload.demand_next_action = demandNextAction;
      await onBlock(payload);
    } finally {
      setSubmitting(false);
    }
  }, [canSubmit, isDispatch, codeUnknown, freeText, comment, code, selectedMeta, usesSubteam, subteam, onBlock]);

  if (!item) return null;

  return (
    <Dialog open={!!item} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        className="bg-[hsl(var(--card))] border-[hsl(var(--border))] w-[95vw] md:w-[680px] max-w-[95vw] max-h-[88vh] overflow-y-auto"
        data-testid="block-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-400">
            <ShieldAlert className="w-5 h-5" /> Report Problem
          </DialogTitle>
          <DialogDescription>
            Flagging a problem on <span className="font-medium text-[hsl(var(--foreground))]">{item.item_description}</span>
            &nbsp;(<span className="font-mono-data text-xs font-semibold"><span className="text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mr-1">PO</span>{item.po_number}</span>)
            &nbsp;— {stage}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-1">
          {/* ─── Dispatch stage: classic free-text only ─── */}
          {isDispatch && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1 block">
                Describe the problem
              </label>
              <textarea
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder="e.g., Carrier delay on Blue Dart, missing packing slip, damage during loading…"
                className="w-full h-28 px-3 py-2 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                autoFocus
                data-testid="block-reason-input"
              />
              <p className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Required — minimum 3 characters.</p>
            </div>
          )}

          {/* ─── All other stages: code-based layout ─── */}
          {!isDispatch && (
            <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-4">
              {/* LEFT: sub-team toggle + code dropdown + guideline */}
              <div className={`space-y-3 ${codeUnknown ? "opacity-40 pointer-events-none" : ""}`}>
                {usesSubteam && (
                  <div>
                    <label className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1.5 block">
                      Sub-team<span className="text-red-500 ml-0.5">*</span>
                    </label>
                    <div className="flex gap-1.5 flex-wrap" role="group" data-testid="subteam-toggle">
                      {Object.keys(stageCfg?.subteams || {}).map(st => (
                        <button
                          key={st}
                          type="button"
                          onClick={() => handleSubteamChange(st)}
                          className={`px-3 py-1.5 rounded-md text-xs font-semibold border transition-colors ${
                            subteam === st
                              ? "bg-red-500/15 border-red-500 text-red-500"
                              : "bg-[hsl(var(--card))] border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-red-500/50"
                          }`}
                          data-testid={`subteam-${st.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {st}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1.5 block">
                    Reason code<span className="text-red-500 ml-0.5">*</span>
                  </label>
                  <Select value={code} onValueChange={setCode} disabled={codeUnknown || (usesSubteam && !subteam)}>
                    <SelectTrigger className="h-9 text-xs bg-[hsl(var(--muted))] border-[hsl(var(--border))]" data-testid="reason-code-select">
                      <SelectValue placeholder={usesSubteam && !subteam ? "Pick a sub-team first" : "Select a reason code"} />
                    </SelectTrigger>
                    <SelectContent>
                      {activeCodes.map(c => (
                        <SelectItem key={c.code} value={c.code}>
                          <span className="font-mono-data mr-2 font-semibold">{c.code}</span>
                          <span className="text-[hsl(var(--muted-foreground))]">{c.description}</span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {selectedMeta && (
                  <div className="rounded-md border border-orange-500/30 bg-orange-500/5 p-3" data-testid="reason-guideline">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-mono-data text-xs font-bold px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-500 border border-orange-500/30">{selectedMeta.code}</span>
                          <span className="text-sm font-semibold text-[hsl(var(--foreground))]">{selectedMeta.description}</span>
                        </div>
                        <p className="text-xs text-[hsl(var(--muted-foreground))] leading-relaxed">
                          {selectedMeta.guideline || <em className="opacity-70">No additional guideline.</em>}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1.5 block">
                    Additional comment <span className="normal-case font-normal text-[hsl(var(--muted-foreground))]/70">(optional)</span>
                  </label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    placeholder="Add any specific details (vendor name, ETA, part serial, etc.)"
                    rows={2}
                    className="w-full px-3 py-2 text-sm rounded-md bg-[hsl(var(--muted))] border border-[hsl(var(--border))] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                    data-testid="reason-comment-input"
                  />
                </div>
              </div>

              {/* RIGHT: "Reason code not known" toggle */}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1.5 block">Can't find it?</label>
                <button
                  type="button"
                  onClick={() => {
                    const next = !codeUnknown;
                    setCodeUnknown(next);
                    if (next) { setCode(""); setSubteam(""); setComment(""); }
                    else { setFreeText(""); }
                  }}
                  className={`w-full h-full min-h-[88px] rounded-md border-2 border-dashed text-xs font-semibold px-3 py-3 transition-colors ${
                    codeUnknown
                      ? "bg-amber-500/10 border-amber-500/60 text-amber-600"
                      : "bg-[hsl(var(--card))] border-[hsl(var(--border))] text-[hsl(var(--muted-foreground))] hover:border-amber-500/40 hover:text-amber-600"
                  }`}
                  data-testid="reason-unknown-btn"
                >
                  <div className="flex flex-col items-center gap-1.5">
                    {codeUnknown ? <Check className="w-4 h-4" /> : <HelpCircle className="w-4 h-4" />}
                    <span>Reason code not known</span>
                    {codeUnknown && (
                      <span className="text-[9px] uppercase tracking-wider opacity-70">Active — type below</span>
                    )}
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* ─── Free-text fallback when "Reason code not known" active ─── */}
          {!isDispatch && codeUnknown && (
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1.5 block">
                Describe the problem<span className="text-red-500 ml-0.5">*</span>
              </label>
              <textarea
                value={freeText}
                onChange={(e) => setFreeText(e.target.value)}
                placeholder="Explain the issue in as much detail as possible."
                className="w-full h-24 px-3 py-2 text-sm rounded-md bg-[hsl(var(--muted))] border border-amber-500/40 text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
                autoFocus
                data-testid="reason-freetext-input"
              />
              <p className="text-[10px] text-amber-600 mt-1">Required when "Reason code not known" is active — minimum 3 characters.</p>
            </div>
          )}

          {/* ─── Waiting on — accountability tagging (optional) ─── */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-widest text-[hsl(var(--muted-foreground))] mb-1.5 block">
              Waiting on <span className="font-normal normal-case tracking-normal">(optional — they get notified)</span>
            </label>
            <div className="flex items-center gap-1.5 flex-wrap rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--muted))]/30 px-2 py-1.5">
              {waitingOn.map(u => (
                <span key={u.id} className="inline-flex items-center gap-1 text-[11.5px] font-medium text-orange-700 dark:text-orange-400 bg-orange-500/5 border border-orange-500/40 rounded-full pl-2 pr-1 py-px rl-pill" data-testid={`block-waiting-chip-${u.id}`}>
                  {u.name}
                  <button type="button" onClick={() => setWaitingOn(list => list.filter(x => x.id !== u.id))} className="inline-flex items-center justify-center w-4 h-4 rounded-full hover:bg-orange-500/15" aria-label={`Remove ${u.name}`}>
                    <X className="w-2.5 h-2.5" />
                  </button>
                </span>
              ))}
              <input
                value={waitQuery}
                onChange={(e) => setWaitQuery(e.target.value)}
                placeholder={waitingOn.length ? "Add another…" : "Type a name…"}
                className="flex-1 min-w-[120px] bg-transparent text-sm focus:outline-none placeholder:text-[hsl(var(--muted-foreground))]/50"
                data-testid="block-waiting-input"
              />
            </div>
            {waitMatches.length > 0 && (
              <div className="mt-1 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--popover))] shadow-md overflow-hidden">
                {waitMatches.map(u => (
                  <button key={u.id} type="button"
                    onClick={() => { setWaitingOn(list => [...list, u]); setWaitQuery(""); }}
                    className="flex items-center gap-2 w-full px-2.5 py-1.5 text-left text-sm hover:bg-[hsl(var(--muted))]/50 transition-colors"
                    data-testid={`block-waiting-option-${u.id}`}>
                    <span className="font-medium">{u.name}</span>
                    <span className="text-[10px] text-[hsl(var(--muted-foreground))]">{u.role}</span>
                  </button>
                ))}
              </div>
            )}
            {waitingOn.length > 0 && (
              <label className="mt-2 inline-flex items-center gap-2 text-[12px] text-[hsl(var(--foreground))]/80 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={demandNextAction}
                  onChange={(e) => setDemandNextAction(e.target.checked)}
                  className="w-3.5 h-3.5 accent-orange-600"
                  data-testid="block-demand-na-checkbox"
                />
                <span className="inline-flex items-center gap-1"><Hourglass className="w-3 h-3 text-amber-600" /> Ask them to specify the next action</span>
              </label>
            )}
          </div>

          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-md text-sm font-medium bg-[hsl(var(--muted))] text-[hsl(var(--foreground))] hover:bg-[hsl(var(--muted))]/80 transition-colors"
              data-testid="block-cancel-button"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-2 rounded-md text-sm font-medium bg-red-600 text-white hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              data-testid="block-confirm-button"
            >
              {submitting ? "Reporting…" : "Report Problem"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
